import { useMutation } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { useTranslations } from "use-intl";

import { hasRole, roleRank } from "@lawline/shared/access/platform";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@lawline/ui/components/alert-dialog";
import { Button } from "@lawline/ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@lawline/ui/components/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@lawline/ui/components/tooltip";

import { useAuth } from "@platform/auth/context";
import {
	cancelInvitationMutation,
	reactivateMemberMutation,
	removeMemberMutation,
	suspendMemberMutation,
} from "../tenant.mutations";
import type { Invitation, Member, RosterRow } from "../tenant.types";
import { EditMemberDialog } from "./edit-member";

// Renders nothing when the viewer has no action, for example on their own row.
export function RowActions({ row }: { row: RosterRow }) {
	if (row.invitation) return <InvitationActions invitation={row.invitation} />;
	if (row.member) return <MemberActions member={row.member} />;
	return null;
}

function Trigger() {
	const t = useTranslations("common.actions");
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<DropdownMenuTrigger asChild>
					<Button variant="ghost" size="icon-xs">
						<MoreHorizontal />
					</Button>
				</DropdownMenuTrigger>
			</TooltipTrigger>
			<TooltipContent>{t("more")}</TooltipContent>
		</Tooltip>
	);
}

function MemberActions({ member }: { member: Member }) {
	const t = useTranslations("tenant.memberActions");
	const { user, role: actorRole, can } = useAuth();
	const remove = useMutation(removeMemberMutation);
	const suspend = useMutation(suspendMemberMutation);
	const reactivate = useMutation(reactivateMemberMutation);
	const [editOpen, setEditOpen] = useState(false);
	const [removeOpen, setRemoveOpen] = useState(false);
	const [suspendOpen, setSuspendOpen] = useState(false);

	const self = member.userId === user?.id;
	const outranks = !actorRole || roleRank(member.role) > roleRank(actorRole);
	const canEdit = !self && !outranks && can({ member: ["update"] });
	const canRemove = !self && !outranks && can({ member: ["delete"] });
	// Owners cannot be suspended (mirrors the server guard).
	const canSuspend =
		!self && !outranks && !hasRole(member.role, "owner") && can({ member: ["update"] });
	const suspended = member.status === "suspended";
	if (!canEdit && !canRemove && !canSuspend) return null;

	return (
		<>
			<DropdownMenu>
				<Trigger />
				<DropdownMenuContent align="end">
					{canEdit && (
						<DropdownMenuItem onClick={() => setEditOpen(true)}>{t("edit")}</DropdownMenuItem>
					)}
					{canSuspend &&
						(suspended ? (
							<DropdownMenuItem onClick={() => reactivate.mutate(member.id)}>
								{t("reactivate")}
							</DropdownMenuItem>
						) : (
							<DropdownMenuItem variant="destructive" onClick={() => setSuspendOpen(true)}>
								{t("suspend")}
							</DropdownMenuItem>
						))}
					{canRemove && (
						<DropdownMenuItem variant="destructive" onClick={() => setRemoveOpen(true)}>
							{t("remove")}
						</DropdownMenuItem>
					)}
				</DropdownMenuContent>
			</DropdownMenu>

			<EditMemberDialog member={member} open={editOpen} onOpenChange={setEditOpen} />

			<AlertDialog open={suspendOpen} onOpenChange={setSuspendOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("suspendConfirm.title", { name: member.name })}</AlertDialogTitle>
						<AlertDialogDescription>{t("suspendConfirm.description")}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("suspendConfirm.cancel")}</AlertDialogCancel>
						<AlertDialogAction variant="destructive" onClick={() => suspend.mutate(member.id)}>
							{t("suspendConfirm.confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("removeConfirm.title", { name: member.name })}</AlertDialogTitle>
						<AlertDialogDescription>{t("removeConfirm.description")}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("removeConfirm.cancel")}</AlertDialogCancel>
						<AlertDialogAction variant="destructive" onClick={() => remove.mutate(member.id)}>
							{t("removeConfirm.confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}

function InvitationActions({ invitation }: { invitation: Invitation }) {
	const t = useTranslations("tenant.memberActions");
	const { can } = useAuth();
	const cancel = useMutation(cancelInvitationMutation);
	if (!can({ invitation: ["cancel"] })) return null;

	return (
		<DropdownMenu>
			<Trigger />
			<DropdownMenuContent align="end">
				<DropdownMenuItem variant="destructive" onClick={() => cancel.mutate(invitation.id)}>
					{t("cancelInvitation")}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
